<template>
  <div>
    <a-row :gutter="{ xs: 8, sm: 16, md: 24 }" class="filters-wrapper">
      <a-col v-if="!embedded" :xs="24" :md="12" :lg="6" class="gutter-row mb-24px">
        <searchable-organization-select
            :value="filters.organization_id"
            @change="filters.organization_id = $event"
        />
      </a-col>
      <a-col v-if="!embedded" :xs="24" :md="12" :lg="6" class="gutter-row mb-24px">
        <form-input
            :value="filters.search"
            :placeholder="$t('label.search')"
            allow-clear
            with-icon
            @input="filters.search = $event"
        >
          <template #prefix-icon>
            <search-icon stroke="#467BFF"/>
          </template>
        </form-input>
      </a-col>
      <a-col :xs="24" :md="12" :lg="6" class="gutter-row mb-24px">
        <form-select
            :placeholder="$t('label.status')"
            :options="statusOptions"
            :value="filters.status"
            allow-clear
            @change="filters.status = $event"
        />
      </a-col>
      <a-col :xs="24" :md="12" :lg="6" class="gutter-row mb-24px">
        <form-select
            :placeholder="$t('label.source')"
            :options="sourceOptions"
            :value="filters.source"
            allow-clear
            @change="filters.source = $event"
        />
      </a-col>
    </a-row>

    <a-alert
        v-if="!filters.organization_id"
        class="mb-24px"
        type="info"
        show-icon
        :message="$t('text.select_organization_to_see_badges')"
    />

    <app-table
        v-else
        :columns="columns"
        :data="badges"
        :current="badgesMeta.current_page"
        :total="badgesMeta.total"
        scroll
        @getData="getTableData"
    >
      <span slot="nfc_icon">
        <identification-badge-id-icon height="16" width="16" stroke="#467BFF" view-box="0 0 16 16"/>
        <span>{{ $t('label.nfc_id') }}</span>
      </span>
      <span slot="holder_icon">
        <span>{{ $t('label.carrier') }}</span>
      </span>
      <span slot="account_icon">
        <span>{{ $t('label.account') }}</span>
      </span>
      <span slot="source_icon">
        <span>{{ $t('label.source') }}</span>
      </span>
      <span slot="status_icon">
        <four-circles-icon height="16" width="16" stroke="#467BFF" view-box="0 0 32 32"/>
        <span>{{ $t('label.status') }}</span>
      </span>
      <span slot="created_at_icon">
        <span>{{ $t('label.created_at') }}</span>
      </span>

      <template #holder="{ record }">{{ holderName(record) || '-' }}</template>
      <template #account="{ record }">{{ record.account ? record.account.email : '-' }}</template>
      <template #source="{ text }">
        <a-tag v-if="text">{{ $t(`badge_source.${text.name}`) }}</a-tag>
        <span v-else>-</span>
      </template>
      <template #status="{ text }">
        <a-tag :color="text && text.name === 'active' ? 'green' : 'red'">
          {{ $t(`status.${(text && text.name) || 'revoked'}`) }}
        </a-tag>
      </template>
      <template #created_at="{ text }">{{ formatDate(text) }}</template>

      <template v-slot:actions="{ record }">
        <div class="d-flex table-actions-wrapper">
          <a-tooltip v-if="record.status && record.status.name === 'active'">
            <template v-slot:title>{{ $t('btn.revoke_badge') }}</template>
            <span class="cursor-pointer" @click="confirmRevoke(record)">
              <trash-icon height="18" width="18" stroke="#467BFF" view-box="0 0 32 32"/>
            </span>
          </a-tooltip>
          <a-tooltip v-else>
            <template v-slot:title>{{ $t('btn.reactivate_badge') }}</template>
            <span class="cursor-pointer" @click="confirmReactivate(record)">
              <power-icon height="18" width="18" stroke="#467BFF" view-box="0 0 16 16"/>
            </span>
          </a-tooltip>
          <a-tooltip>
            <template v-slot:title>{{ $t('btn.edit_badge') }}</template>
            <span class="cursor-pointer" @click="editBadge(record)">
              <pencil-line-edit-icon height="16" width="16" stroke="#467BFF" view-box="0 0 16 16"/>
            </span>
          </a-tooltip>
        </div>
      </template>

      <template #footer-actions>
        <div class="d-flex" style="gap: 12px; align-items: center;">
          <span v-if="badgesPushedAt" class="badges-pushed-at">
            {{ $t('text.badges_last_pushed') }}: {{ formatDateTime(badgesPushedAt) }}
          </span>
          <a-button class="app-button outline-primary" @click="openCreateModal">
            {{ $t('btn.create_badge') }}
          </a-button>
          <a-button class="app-button primary" :loading="pushing" @click="pushToStations">
            {{ $t('btn.push_to_stations') }}
          </a-button>
        </div>
      </template>
    </app-table>

    <badge-modal
        :show="showBadgeModal"
        :badge-reference="editableBadgeReference"
        :organization-id="filters.organization_id"
        @close="onBadgeModalClose"
    />
  </div>
</template>

<script>
import _ from 'lodash';
import {mapGetters} from 'vuex';
import {settings} from '@/core/defaultSettings';
import generalMixin from '@/mixins/general';
import {pushBadges} from '@/api/organizations';
import AppTable from '@/components/elements/AppTable';
import FormInput from '@/components/elements/FormInput';
import FormSelect from '@/components/elements/FormSelect';
import SearchableOrganizationSelect from '@/components/elements/SearchableOrganizationSelect.vue';
import BadgeModal from './modals/BadgeModal';
import FourCirclesIcon from '@/assets/icons/fourCirclesIcon';
import IdentificationBadgeIdIcon from '@/assets/icons/identificationBadgeIdIcon';
import PencilLineEditIcon from '@/assets/icons/pencilLineEditIcon';
import PowerIcon from '@/assets/icons/powerIcon';
import SearchIcon from '@/assets/icons/searchIcon';
import TrashIcon from '@/assets/icons/trashIcon';

// Mirrors App\Models\Badge::STATUSES / SOURCES on the backend.
const BADGE_STATUSES = [{value: 1, name: 'active'}, {value: 2, name: 'revoked'}];
const BADGE_SOURCES = [
  {value: 1, name: 'import_manual'},
  {value: 2, name: 'import_remote'},
  {value: 3, name: 'ui'},
  {value: 4, name: 'api'},
];

export default {
  components: {
    AppTable,
    FormInput,
    FormSelect,
    SearchableOrganizationSelect,
    BadgeModal,
    FourCirclesIcon,
    IdentificationBadgeIdIcon,
    PencilLineEditIcon,
    PowerIcon,
    SearchIcon,
    TrashIcon,
  },
  mixins: [generalMixin],
  props: {
    // Rendue à l'intérieur d'une autre page (onglet Badges de la page Utilisateurs) :
    // l'organisation et la recherche viennent alors du parent, on masque les nôtres.
    embedded: {type: Boolean, default: false},
    organizationId: {type: Number, default: null},
    search: {type: String, default: null},
  },
  data: () => ({
    showBadgeModal: false,
    editableBadge: null,
    pushing: false,
    filters: {
      organization_id: null,
      search: null,
      status: null,
      source: null,
    },
  }),
  mounted() {
    if (this.embedded) {
      this.filters.organization_id = this.organizationId;
      this.filters.search = this.search;
      return;
    }
    this.$store.dispatch('organizationsStore/GET_DROPDOWN_LIST');
    if (this.$route.query && this.$route.query.organization_id) {
      this.filters.organization_id = parseInt(this.$route.query.organization_id);
      return;
    }
    // Un administrateur n'appartient qu'à une organisation : sans pré-sélection il
    // tombe sur « sélectionnez une organisation » alors qu'il n'a pas de choix à faire.
    if (this.userOrganizationIds && this.userOrganizationIds.length === 1) {
      this.filters.organization_id = this.userOrganizationIds[0];
    }
  },
  computed: {
    ...mapGetters({
      badges: 'badgesStore/BADGES',
      badgesMeta: 'badgesStore/BADGES_META',
      badgesPushedAt: 'badgesStore/BADGES_PUSHED_AT',
      userOrganizationIds: 'getOrganizationIds',
    }),
    statusOptions() {
      return BADGE_STATUSES.map(s => ({value: s.value, label: this.$t(`status.${s.name}`)}));
    },
    sourceOptions() {
      return BADGE_SOURCES.map(s => ({value: s.value, label: this.$t(`badge_source.${s.name}`)}));
    },
    editableBadgeReference() {
      return this.editableBadge ? this.editableBadge.reference : null;
    },
    columns() {
      return [
        {dataIndex: 'nfc', slots: {title: 'nfc_icon'}, scopedSlots: {customRender: 'nfc'}},
        {dataIndex: 'holder', slots: {title: 'holder_icon'}, scopedSlots: {customRender: 'holder'}},
        {dataIndex: 'account', slots: {title: 'account_icon'}, scopedSlots: {customRender: 'account'}},
        {dataIndex: 'source', slots: {title: 'source_icon'}, scopedSlots: {customRender: 'source'}},
        {dataIndex: 'status', slots: {title: 'status_icon'}, scopedSlots: {customRender: 'status'}},
        {dataIndex: 'created_at', slots: {title: 'created_at_icon'}, scopedSlots: {customRender: 'created_at'}},
        {title: '', dataIndex: 'actions', scopedSlots: {customRender: 'actions'}},
      ];
    },
  },
  watch: {
    // En mode intégré, l'organisation et la recherche sont pilotées par la page parente.
    organizationId(id) {
      if (this.embedded) this.filters.organization_id = id;
    },
    search(value) {
      if (this.embedded) this.filters.search = value;
    },
    filters: {
      immediate: true,
      handler: _.debounce(function () {
        this.getTableData();
      }, settings.debounceTime),
      deep: true,
    },
  },
  methods: {
    getTableData(pagination) {
      // The list is always org-scoped; skip fetching until an organization is chosen.
      if (!this.filters.organization_id) {
        return;
      }
      this.$store.dispatch('badgesStore/GET_BADGES', {...this.filters, ...pagination});
    },
    holderName(record) {
      const name = [record.holder_first_name, record.holder_last_name].filter(Boolean).join(' ');
      return name || record.holder_email;
    },
    formatDate(iso) {
      return iso ? new Date(iso).toLocaleDateString() : '-';
    },
    formatDateTime(ms) {
      return ms ? new Date(ms).toLocaleString() : '-';
    },
    openCreateModal() {
      this.editableBadge = null;
      this.showBadgeModal = true;
    },
    editBadge(badge) {
      this.editableBadge = badge;
      this.showBadgeModal = true;
    },
    onBadgeModalClose(refresh) {
      this.showBadgeModal = false;
      this.editableBadge = null;
      if (refresh) this.getTableData();
    },
    confirmRevoke(badge) {
      this.$confirm({
        title: this.$t('modal.revoke_badge'),
        centered: true,
        closable: true,
        okType: 'danger',
        cancelText: this.$t('btn.no'),
        okText: this.$t('btn.yes'),
        onOk: () => {
          this.$store.dispatch('badgesStore/REVOKE_BADGE', badge.reference)
              .then(() => {
                this.getTableData();
                this.$message.success(this.$t('alert.badge_revoked'));
              })
              .catch(() => this.$message.error(this.$t('alert.something_goes_wrong')));
        },
      });
    },
    confirmReactivate(badge) {
      this.$confirm({
        title: this.$t('modal.reactivate_badge'),
        centered: true,
        closable: true,
        okType: 'primary',
        cancelText: this.$t('btn.no'),
        okText: this.$t('btn.yes'),
        onOk: () => {
          this.$store.dispatch('badgesStore/REACTIVATE_BADGE', badge.reference)
              .then(() => {
                this.getTableData();
                this.$message.success(this.$t('alert.badge_reactivated'));
              })
              .catch(() => this.$message.error(this.$t('alert.something_goes_wrong')));
        },
      });
    },
    pushToStations() {
      if (!this.filters.organization_id) return;
      this.pushing = true;
      pushBadges(this.filters.organization_id)
          .then(() => {
            this.$message.success(this.$t('alert.badges_pushed'));
            this.getTableData(); // refresh so the "last pushed" timestamp reflects this push
          })
          .catch(() => this.$message.error(this.$t('alert.something_goes_wrong')))
          .finally(() => (this.pushing = false));
    },
  },
};
</script>

<style lang="scss" scoped>
.table-actions-wrapper {
  gap: 12px;
}
.badges-pushed-at {
  margin-right: auto;
  color: #6886A6;
  font-size: 14px;
}
</style>
